// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

import Image from 'next/image';
import { useContext } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ImageWithFallback } from '@/components/image-with-fallback';
import { InfoContext } from '@/context/info';
import { joinSgcmMedia } from '@/utils/url/join/public';
import { getInternalLink } from '@/utils/url/utm';

import { SlideNextButton } from './slide-next-button';
import { SlidePrevButton } from './slide-prev-button';
import styles from './styles.module.scss';
import type { Component } from './types';

export const ThumbnailsNavigator: Component = ({
  images,
  onBoxClick,
}) => {
  const { carDetail } = useContext(InfoContext);
  const has360Submodel = carDetail.subModels
    .find(submodel => submodel.has360);

  const handleClick = (index: number) => () => {
    onBoxClick?.(index);
  };

  return (
    <div
      className={styles.containerRoot}
      data-tracking-selector="gallery">
      {images.length !== 0
        ? (
          <Swiper
            modules={[Navigation, Pagination]}
            navigation={{
              nextEl: `.${styles.buttonNext}`,
              prevEl: `.${styles.buttonPrev}`,
            }}
            pagination={{
              type: 'fraction',
              horizontalClass: styles.containerFaction,
            }}>
            {images.map((image, index) => (
              <SwiperSlide
                key={image}
                onClick={handleClick(index)}>
                <ImageWithFallback
                  alt={image}
                  className={styles.image}
                  fallback={`${image}.jpg`}
                  height={0}
                  src={`${image}_b.jpg`}
                  width={0} />
                {has360Submodel && index === 0 && (
                  <a
                    className={styles.container360ViewBadge}
                    href={`${getInternalLink(
                      `https://i.i-sgcm.com/new_cars/pano360_viewer.php?specCode=${has360Submodel.id}&carCode=${carDetail.id}`, // @suppress longLineCheck
                      'carinfo',
                    )}`}
                    rel="noreferrer"
                    target="_blank"
                    onClick={e => e.stopPropagation()}>
                    <Image
                      alt="360 view"
                      height={36}
                      src={`${joinSgcmMedia('/public/new-cars/icon/iconHas360.svg')}`}
                      width={36} />
                  </a>
                )}
              </SwiperSlide>
            ))}
            <SlideNextButton className={styles.buttonNext} />
            <SlidePrevButton className={styles.buttonPrev} />
          </Swiper>
        )
        : (
          <ImageWithFallback
            alt={`${carDetail.make} ${carDetail.model}`}
            className={styles.imageFallback}
            height={0}
            src={carDetail.image.thumbnailUrl}
            width={0} />
        )}
    </div>
  );
};
